import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const CategoryFilter = ({
  selectedFilters = {},
  onFilterChange,
  availableFilters = {},
  resultCount = 0,
  onClearAll,
  variant = 'default' // 'default', 'sidebar', 'mobile'
}) => {
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    location: true,
    price: true,
    cultural: true
  });
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const mobileFiltersRef = useRef(null);

  // Use availableFilters from props, or empty objects if not provided
  const filters = Object.keys(availableFilters).length > 0 ? availableFilters : {
    location: [],
    price: [],
    cultural: []
  };

  const handleFilterToggle = (section, filterId) => {
    const currentFilters = selectedFilters[section] || [];
    const newFilters = currentFilters.includes(filterId)
      ? currentFilters.filter(id => id !== filterId)
      : [...currentFilters, filterId];

    onFilterChange?.({
      ...selectedFilters,
      [section]: newFilters
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getActiveFilterCount = () => {
    return Object.values(selectedFilters).reduce((total, filters) => total + (filters?.length || 0), 0);
  };

  const handleClearAll = () => {
    onClearAll?.();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileFiltersRef.current && !mobileFiltersRef.current.contains(event.target)) {
        setShowMobileFilters(false);
      }
    };

    if (showMobileFilters) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [showMobileFilters]);

  const FilterSection = ({ sectionKey, title, icon, items }) => (
    <div className="border-b border-border-light last:border-b-0">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between p-4 hover:bg-surface-50 transition-smooth"
      >
        <div className="flex items-center space-x-3">
          <Icon name={icon} size={18} color="#6B5B4F" />
          <span className="font-medium text-text-primary">{title}</span>
          {selectedFilters[sectionKey]?.length > 0 && (
            <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full font-data">
              {selectedFilters[sectionKey].length}
            </span>
          )}
        </div>
        <Icon
          name={expandedSections[sectionKey] ? "ChevronUp" : "ChevronDown"}
          size={16}
          color="#6B5B4F"
        />
      </button>

      {expandedSections[sectionKey] && (
        <div className="px-4 pb-4 space-y-2 animate-slide-down">
          {items.map((item) => (
            <label
              key={item.id}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-surface-50 cursor-pointer transition-smooth"
            >
              <input
                type="checkbox"
                checked={selectedFilters[sectionKey]?.includes(item.id) || false}
                onChange={() => handleFilterToggle(sectionKey, item.id)}
                className="w-4 h-4 text-primary border-border-dark rounded focus:ring-primary focus:ring-2"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-text-primary">{item.label}</span>
                  <span className="text-xs text-text-muted font-data">({item.count})</span>
                </div>
                <div className="text-xs text-text-secondary font-caption">{item.cultural}</div>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  if (variant === 'mobile') {
    return (
      <>
        {/* Mobile Filter Trigger */}
        <div className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-border">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              iconName="Filter"
              iconSize={16}
              onClick={() => setShowMobileFilters(true)}
            >
              Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
            </Button>

            <div className="text-sm text-text-secondary font-caption">
              {resultCount} vendors found
            </div>
          </div>

          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-text-secondary hover:text-primary"
            >
              Clear All
            </Button>
          )}
        </div>

        {/* Mobile Filter Overlay */}
        {showMobileFilters && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/50">
            <div
              ref={mobileFiltersRef}
              className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-strong animate-slide-in-right overflow-y-auto"
            >
              {/* Mobile Filter Header */}
              <div className="flex items-center justify-between p-4 border-b border-border bg-surface-50">
                <h3 className="font-heading font-semibold text-lg text-text-primary">Filters</h3>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="flex items-center justify-center w-8 h-8 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-100 transition-smooth"
                >
                  <Icon name="X" size={20} color="currentColor" />
                </button>
              </div>

              {/* Mobile Filter Content */}
              <div className="flex-1">
                <FilterSection sectionKey="category" title="Category" icon="Grid3X3" items={filters.category} />
                <FilterSection sectionKey="location" title="Location" icon="MapPin" items={filters.location} />
                <FilterSection sectionKey="price" title="Price Range" icon="DollarSign" items={filters.price} />
                <FilterSection sectionKey="cultural" title="Cultural Specialization" icon="Heart" items={filters.cultural} />
              </div>

              {/* Mobile Filter Footer */}
              <div className="p-4 border-t border-border bg-surface-50">
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="md"
                    onClick={handleClearAll}
                    fullWidth
                    disabled={getActiveFilterCount() === 0}
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => setShowMobileFilters(false)}
                    fullWidth
                  >
                    Show {resultCount} Results
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div className="hidden lg:block w-80 bg-white border-r border-border h-full overflow-y-auto">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-border bg-surface-50">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-lg text-text-primary">Filters</h3>
            {getActiveFilterCount() > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="text-text-secondary hover:text-primary"
              >
                Clear All
              </Button>
            )}
          </div>
          <div className="mt-2 text-sm text-text-secondary font-caption">
            {resultCount} vendors found
          </div>
        </div>

        {/* Sidebar Content */}
        <div className="flex-1">
          <FilterSection sectionKey="category" title="Category" icon="Grid3X3" items={filters.category} />
          <FilterSection sectionKey="location" title="Location" icon="MapPin" items={filters.location} />
          <FilterSection sectionKey="price" title="Price Range" icon="DollarSign" items={filters.price} />
          <FilterSection sectionKey="cultural" title="Cultural Specialization" icon="Heart" items={filters.cultural} />
        </div>
      </div>
    );
  }

  // Default horizontal chips variant
  return (
    <div className="bg-white border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <h3 className="font-heading font-semibold text-text-primary">Filter Results</h3>
            <div className="text-sm text-text-secondary font-caption">
              {resultCount} vendors found
            </div>
          </div>

          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-text-secondary hover:text-primary"
            >
              Clear All ({getActiveFilterCount()})
            </Button>
          )}
        </div>

        {/* Filter Chips */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(filters).map(([sectionKey, items]) => (
            items.map((item) => (
              <button
                key={`${sectionKey}-${item.id}`}
                onClick={() => handleFilterToggle(sectionKey, item.id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-full text-sm font-medium transition-smooth border ${selectedFilters[sectionKey]?.includes(item.id)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-surface-100 text-text-secondary border-border hover:bg-surface-200 hover:text-primary'
                  }`}
              >
                <span>{item.label}</span>
                <span className="text-xs opacity-75">({item.count})</span>
                {selectedFilters[sectionKey]?.includes(item.id) && (
                  <Icon name="X" size={14} color="currentColor" />
                )}
              </button>
            ))
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryFilter;